import { h } from "../dom.js";

const PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/**
 * Current calendar month (`YYYY-MM`) from the clock.
 * Page lanes adopt this instead of a hard-coded month. The month is local
 * to `now` so a test can pass a fixed Date without touching the timezone.
 */
export function defaultPeriod(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${String(year)}-${month}`;
}

/**
 * The local month, but never later than the UTC month (rd-x2).
 *
 * For routes that refuse a period after the current UTC month. The hub-intake
 * contract says "Not after it" (books-api.yaml), so on the 1st in a zone ahead
 * of UTC the local month is still the future to the Worker, and a page asking
 * for it gets an error. Clamping keeps such a page on the month the Worker can
 * answer. Behind UTC (US evenings at month end) the local month is already the
 * earlier one and is used as is. Home and Receipts both use this, so they
 * always count the same month.
 */
export function periodNotAfterUtc(now: Date = new Date()): string {
  const local = defaultPeriod(now);
  const utc = utcPeriod(now);
  return local < utc ? local : utc;
}

/**
 * The current UTC month (`YYYY-MM`): the month the hub-intake route counts its
 * bounds from, both "not after it" and "at most 23 months before it".
 */
export function utcPeriod(now: Date = new Date()): string {
  return `${String(now.getUTCFullYear())}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** Format `YYYY-MM` as `June 2026`. */
export function formatPeriodLabel(period: string): string {
  const [y, m] = period.split("-");
  const month = Number(m);
  if (!y || !month || month < 1 || month > 12) return period;
  return `${MONTH_NAMES[month - 1] ?? period} ${y}`;
}

/** Shift a `YYYY-MM` period by `delta` months (client-side only). */
export function shiftPeriod(period: string, delta: number): string {
  if (!PERIOD_RE.test(period)) return period;
  const [ys, ms] = period.split("-");
  const year = Number(ys);
  const month = Number(ms);
  const date = new Date(Date.UTC(year, month - 1 + delta, 1));
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${String(y)}-${m}`;
}

export function isValidPeriod(period: string): boolean {
  return PERIOD_RE.test(period);
}

export interface PeriodControlOptions {
  period: string;
  ariaLabel?: string;
  onChange: (period: string) => void;
}

/**
 * App-wide period control: ‹ June 2026 › chevrons + editable month label
 * (docs/design/console-ui-design.md §6).
 */
export function createPeriodControl(opts: PeriodControlOptions): HTMLElement {
  let period = opts.period;

  const labelInput = h("input", {
    type: "month",
    class: "period-label-edit period-input",
    value: period,
    "aria-label": opts.ariaLabel ?? "Period",
    title: formatPeriodLabel(period),
  });

  const prev = h(
    "button",
    {
      type: "button",
      class: "period-chevron",
      "data-action": "period-prev",
      "aria-label": "Previous month",
    },
    ["‹"],
  );
  const next = h(
    "button",
    {
      type: "button",
      class: "period-chevron",
      "data-action": "period-next",
      "aria-label": "Next month",
    },
    ["›"],
  );

  const sync = (): void => {
    labelInput.value = period;
    labelInput.title = formatPeriodLabel(period);
  };

  const commit = (nextPeriod: string): void => {
    if (!isValidPeriod(nextPeriod) || nextPeriod === period) {
      sync();
      return;
    }
    period = nextPeriod;
    sync();
    opts.onChange(period);
  };

  prev.addEventListener("click", () => {
    commit(shiftPeriod(period, -1));
  });
  next.addEventListener("click", () => {
    commit(shiftPeriod(period, 1));
  });
  labelInput.addEventListener("change", () => {
    commit(labelInput.value);
  });

  return h("div", { class: "period-control", "data-role": "period-control" }, [
    prev,
    labelInput,
    next,
  ]);
}
